import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, ReactNode } from 'react';
import { LoginResponse, UserRole } from '../types/api';

// Redundant storage helper functions for admin JWT storage
const setAdminToken = (value: string) => {
  const expires = new Date();
  expires.setTime(expires.getTime() + (365 * 24 * 60 * 60 * 1000)); // 1 year
  
  // Store in multiple cookies with different names
  document.cookie = `gymAuth1=${value}; expires=${expires.toUTCString()}; path=/; secure; samesite=strict`;
  document.cookie = `sessionData=${value}; expires=${expires.toUTCString()}; path=/; secure; samesite=strict`;
  document.cookie = `appToken=${value}; expires=${expires.toUTCString()}; path=/; secure; samesite=strict`;
  
  // Store in localStorage and sessionStorage
  localStorage.setItem('gymAppState', value);
  localStorage.setItem('adminBackup', value);
  sessionStorage.setItem('userSession', value);
  
  // Store in IndexedDB (async but don't wait for it)
  try {
    const request = indexedDB.open('gymAuthDB', 1);
    request.onupgradeneeded = (event: any) => {
      const db = event.target.result;
      if (!db.objectStoreNames.contains('auth')) {
        db.createObjectStore('auth');
      }
    };
    request.onsuccess = (event: any) => {
      const db = event.target.result;
      const transaction = db.transaction(['auth'], 'readwrite');
      const store = transaction.objectStore('auth');
      store.put(value, 'adminToken');
      store.put(value, 'backupToken');
    };
  } catch (error) {
    console.error('IndexedDB storage failed:', error);
  }
};

const getAdminToken = (): string | null => {
  // Check all storage locations, return first found
  
  // Check cookies
  const cookieChecks = ['gymAuth1=', 'sessionData=', 'appToken='];
  for (const cookiePrefix of cookieChecks) {
    const value = document.cookie
      .split('; ')
      .find(row => row.startsWith(cookiePrefix))
      ?.split('=')[1];
    if (value) return value;
  }
  
  // Check localStorage
  const localStorageValue = localStorage.getItem('gymAppState') || localStorage.getItem('adminBackup');
  if (localStorageValue) return localStorageValue;
  
  // Check sessionStorage
  const sessionStorageValue = sessionStorage.getItem('userSession');
  if (sessionStorageValue) return sessionStorageValue;
  
  return null;
};

const deleteAdminToken = () => {
  // Clear all storage locations
  document.cookie = 'gymAuth1=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
  document.cookie = 'sessionData=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
  document.cookie = 'appToken=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
  
  localStorage.removeItem('gymAppState');
  localStorage.removeItem('adminBackup');
  sessionStorage.removeItem('userSession');
  
  try {
    const request = indexedDB.open('gymAuthDB', 1);
    request.onsuccess = (event: any) => {
      const db = event.target.result;
      const transaction = db.transaction(['auth'], 'readwrite');
      const store = transaction.objectStore('auth');
      store.delete('adminToken');
      store.delete('backupToken');
    };
  } catch (error) {
    console.error('IndexedDB deletion failed:', error);
  }
};

interface AuthContextType {
  user: LoginResponse | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (userData: LoginResponse) => void;
  logout: () => void;
  isAdmin: () => boolean;
  isClient: () => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<LoginResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Check for stored auth token and user data on mount
    const restoreAuthState = () => {
      const userData = localStorage.getItem('user');
      
      if (userData) {
        try {
          const parsedUser = JSON.parse(userData);
          
          // For admin users, always try to restore (since tokens never expire)
          if (parsedUser.role === UserRole.Admin) {
            const token = getAdminToken();
            if (token) {
              setUser(parsedUser);
            } else {
              // Token not found, clear user data
              localStorage.removeItem('user');
              deleteAdminToken();
            }
          } else {
            // For clients, check expiration as before
            if (new Date(parsedUser.expiresAt) > new Date()) {
              const token = localStorage.getItem('authToken');
              if (token) {
                setUser(parsedUser);
              } else {
                // Token not found, clear user data
                localStorage.removeItem('user');
                localStorage.removeItem('authToken');
              }
            } else {
              // Token expired, clear storage
              localStorage.removeItem('user');
              localStorage.removeItem('authToken');
            }
          }
        } catch (error) {
          console.error('Error parsing user data:', error);
          localStorage.removeItem('user');
          localStorage.removeItem('authToken');
          deleteAdminToken();
        }
      }
    };

    // Initial auth state restoration
    restoreAuthState();
    setIsLoading(false);

    // Add page visibility listener for mobile resume
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        // Tab became active - always try to restore auth from storage
        restoreAuthState();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  const login = useCallback((userData: LoginResponse) => {
    setUser(userData);

    if (userData.role === UserRole.Admin) {
      // Admin: store token in multiple locations, user data in localStorage
      setAdminToken(userData.token);
      localStorage.setItem('user', JSON.stringify(userData));
    } else {
      // Client: use localStorage for both (unchanged)
      localStorage.setItem('authToken', userData.token);
      localStorage.setItem('user', JSON.stringify(userData));
    }

    // Dispatch custom event to notify other components of login
    window.dispatchEvent(new CustomEvent('userLoggedIn'));
  }, []);

  const logout = useCallback(() => {
    setUser(null);
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
    deleteAdminToken();
  }, []);

  const isAuthenticated = !!user;

  const isAdmin = useCallback(() => user?.role === UserRole.Admin, [user]);

  const isClient = useCallback(() => user?.role === UserRole.Client, [user]);

  const value: AuthContextType = useMemo(() => ({
    user,
    isAuthenticated,
    isLoading,
    login,
    logout,
    isAdmin,
    isClient,
  }), [user, isAuthenticated, isLoading, login, logout, isAdmin, isClient]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}; 